import { useMutation } from '@apollo/client';
import { OrganizationInvitationError, OrganizationInvitationErrorSchema, getExtendedErrors } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import { AlertModalLayout, Button, GeneralFormError, Modal } from '@ui-components/neo.tax';
import { useState } from 'react';

import {
  GET_COMPANIES,
  GetCompaniesForUserQueryVariables,
  GET_USER,
  GetUserQueryVariables,
  RESEND_ORGANIZATION_INVITATION,
  ResendOrganizationInvitationMutation,
  ResendOrganizationInvitationMutationVariables,
} from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';
import { OrganizationMembersTableItemInvitation } from '@neo.tax/shared/types';

interface ResendInvitationModalProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  invitation: OrganizationMembersTableItemInvitation | undefined;
  onResend: () => void;
}

export const ResendInvitationModal = ({
  invitation,
  isOpen,
  onResend,
  setIsOpen,
}: ResendInvitationModalProps): React.ReactElement | null => {
  const { user } = useCurrentUser();
  const [errorMessage, setErrorMessage] = useState<string>();

  // Refetch the org-member lists so the resent invitation's derivedState flips EXPIRED → PENDING
  // and its fresh expiry is reflected in place. Mirrors EditOrganizationMemberModal.
  const [resendOrganizationInvitation, { loading }] = useMutation<
    ResendOrganizationInvitationMutation,
    ResendOrganizationInvitationMutationVariables
  >(RESEND_ORGANIZATION_INVITATION, {
    refetchQueries: user
      ? [
          { query: GET_USER, variables: { userId: user.id } satisfies GetUserQueryVariables },
          { query: GET_COMPANIES, variables: { userId: user.id } satisfies GetCompaniesForUserQueryVariables },
        ]
      : [],
    awaitRefetchQueries: true,
  });

  const resend = async (): Promise<void> => {
    if (!invitation) {
      return;
    }
    try {
      await resendOrganizationInvitation({ variables: { organizationInvitationId: invitation.id } });
      onResend();
      setIsOpen(false);
    } catch (e) {
      // Surface backend-coded invitation errors verbatim — notably the 60s resend rate limit —
      // and only page Sentry for genuinely unexpected failures.
      let invitationErrors: OrganizationInvitationError[] = [];
      try {
        invitationErrors = getExtendedErrors(e, OrganizationInvitationErrorSchema);
      } catch {
        // Not an invitation-coded error.
      }
      if (invitationErrors.length > 0) {
        setErrorMessage(invitationErrors[0].description);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
        Sentry.captureException(e);
      }
    }
  };

  const onClickCancel = (): void => {
    setIsOpen(false);
    setErrorMessage(undefined);
  };

  if (!invitation) {
    return null;
  }
  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <AlertModalLayout
        title={`Resend invitation to "${invitation.email}"`}
        copy={`Send a fresh invitation email to ${invitation.email}? This resets the invitation's expiration.`}
        trailingContent={
          <>
            <Button size="base" variant="white" rounded="regular" onClick={onClickCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              size="base"
              variant="primary"
              rounded="regular"
              type="button"
              disabled={loading}
              onClick={(): void => void resend()}
            >
              {loading ? 'Resending...' : 'Resend Invitation'}
            </Button>
          </>
        }
      >
        <GeneralFormError className="text-right" error={!!errorMessage}>
          {errorMessage}
        </GeneralFormError>
      </AlertModalLayout>
    </Modal>
  );
};
